import { Injectable, SecurityContext } from '@angular/core';
import { BehaviorSubject, Observable, throwError, Subscription, of, from as fromPromise, Subject, timer, firstValueFrom } from 'rxjs';
import { catchError, switchMap, tap, finalize, map, mergeMap, filter, take, first, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ChatSession } from '../models/chat-session.model';
import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service'; // Import AppUser
import { AppUser } from '../users/user.model';
import { ChatMessage } from '../models/chat-message.model'; // Import ChatMessage
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IndexedDbService } from './indexed-db.service'; // Import IndexedDbService
import { MessageSegment } from '../models/message-segment.model';
import { SessionCanvasRouteRecord } from '../models/session-canvas-route-record.model';

@Injectable({
  providedIn: 'root'
})
export class ChatSessionService {

  private sessionsSubject = new BehaviorSubject<ChatSession[]>([]);
  sessions$: Observable<ChatSession[]> = this.sessionsSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoadingSessions$: Observable<boolean> = this.isLoadingSubject.asObservable();

  // Emits true once the initial attempt to load sessions for a user is done
  private initialLoadAttemptedSubject = new BehaviorSubject<boolean>(false);
  initialLoadComplete$: Observable<boolean> = this.initialLoadAttemptedSubject.asObservable();

  private activeSessionIdSubject = new BehaviorSubject<number | null>(null);
  activeSessionId$: Observable<number | null> = this.activeSessionIdSubject.asObservable();

  // NEW: Subject to announce when a message has been added and processed.
  private messageAddedSubject = new Subject<ChatMessage>();
  messageAdded$ = this.messageAddedSubject.asObservable();

  private baseUrl = `${environment.restApiUrl}chat/sessions`; // e.g., http://localhost:8080/api/chat/sessions
  private currentAppUser: AppUser | null = null;
  private authSubscription: Subscription;

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthenticationService,
    private sanitizer: DomSanitizer,
    private indexedDbService: IndexedDbService // Inject IndexedDbService
  ) {
    this.authSubscription = this.authService.currentUser$.subscribe(appUser => {
      if (appUser) {
        this.currentAppUser = appUser;
        // Load sessions for the current user using their ID
        this.loadSessionsForCurrentUser().subscribe();
      } else {
        this.currentAppUser = null;
        this.sessionsSubject.next([]); // Clear sessions if user logs out
        this.activeSessionIdSubject.next(null);
        this.initialLoadAttemptedSubject.next(true); // Consider load attempt done for "no user"
      }
    });
  }

  setActiveSessionId(sessionId: number | null): void {
    this.activeSessionIdSubject.next(sessionId);
  }

  getActiveSessionIdSnapshot(): number | null {
    return this.activeSessionIdSubject.getValue();
  }

  private getCurrentUserId(): number | null {
    return typeof this.currentAppUser?.id === 'number' ? this.currentAppUser.id : null;
  }

  private buildCanvasRouteMap(records: SessionCanvasRouteRecord[]): Map<number, string> {
    return new Map(records.map(record => [record.sessionId, record.path]));
  }

  private applyStoredCanvasPath(session: ChatSession, canvasPath: string | null | undefined): ChatSession {
    if (!canvasPath || session.activeCanvasPath === canvasPath) {
      return session;
    }

    return {
      ...session,
      activeCanvasPath: canvasPath,
    };
  }

  private applyStoredCanvasPaths(sessions: ChatSession[], records: SessionCanvasRouteRecord[]): ChatSession[] {
    const canvasRouteMap = this.buildCanvasRouteMap(records);
    return sessions.map(session => this.applyStoredCanvasPath(session, canvasRouteMap.get(session.sessionId)));
  }

  private async getStoredCanvasRoutesForCurrentUser(): Promise<SessionCanvasRouteRecord[]> {
    const currentUserId = this.getCurrentUserId();
    if (currentUserId === null) {
      return [];
    }

    return this.indexedDbService.getSessionCanvasRoutesForUser(currentUserId);
  }

  private async getStoredCanvasPathForSession(sessionId: number): Promise<string | null> {
    const currentUserId = this.getCurrentUserId();
    if (currentUserId === null) {
      return null;
    }

    const record = await this.indexedDbService.getSessionCanvasRoute(currentUserId, sessionId);
    return record?.path ?? null;
  }

  private async applyStoredCanvasPathForCurrentUser(session: ChatSession): Promise<ChatSession> {
    return this.applyStoredCanvasPath(session, await this.getStoredCanvasPathForSession(session.sessionId));
  }

  private updateSessions() {
    // This method might not be needed if operations directly update the subject
    // or if we always fetch the fresh list after modifications.
    // For now, individual methods will update the subject.
  }

  loadSessionsForCurrentUser(): Observable<ChatSession[]> {
    return this.indexedDbService.dbReady$.pipe(
      filter(ready => ready), // Wait until DB is ready
      take(1), // Proceed once
      switchMap(() => {
        if (!this.currentAppUser || !this.currentAppUser.id) {
          this.sessionsSubject.next([]);
          this.initialLoadAttemptedSubject.next(true);
          return of([]);
        }
        this.isLoadingSubject.next(true);
        this.initialLoadAttemptedSubject.next(false);

        // 1. Load from IndexedDB first
        return fromPromise(
          // Get cached sessions, the latest update timestamp, and locally persisted canvas routes concurrently.
          Promise.all([
            this.indexedDbService.getCachedSessions(),
            this.indexedDbService.getLatestSessionUpdateTimestamp(),
            this.getStoredCanvasRoutesForCurrentUser()
          ])
        ).pipe(
          tap(([cachedSessions, lastUpdatedAt, canvasRouteRecords]) => {
            if (cachedSessions.length > 0) {
              const processedSessions = this.applyStoredCanvasPaths(
                cachedSessions.map(s => this.ensureDateObjectsAndProcessSegments(s, false)),
                canvasRouteRecords
              );
              this.sessionsSubject.next(processedSessions);
            }
          }),
          // 2. Then fetch from server and sync
          mergeMap(([cachedSessions, lastUpdatedAt, canvasRouteRecords]) => {
            const canvasRouteMap = this.buildCanvasRouteMap(canvasRouteRecords);
            let params = {};
            if (lastUpdatedAt) {
              params = { lastUpdatedAt: lastUpdatedAt.toString() };
            }
            return this.http.get<ChatSession[]>(`${this.baseUrl}/user/${this.currentAppUser!.id}`, { params }).pipe(
              map(serverSessions => serverSessions.map(s => this.ensureDateObjectsAndProcessSegments(s, false))),
              tap(async serverSessions => {
                // Get the current state of sessions from the BehaviorSubject
                // This ensures we're merging with the most up-to-date in-memory list
                // which might have been affected by other operations like getSession.
                let currentSubjectSessions = this.sessionsSubject.getValue();
                let combinedSessionsMap = new Map<number, ChatSession>(
                  currentSubjectSessions.map(s => [s.sessionId, { ...s, chatMessages: [] }])
                );

                // Add or update sessions from the server

                for (const serverSession of serverSessions) { // serverSession has metadata and *only new* messages
                  const existingSessionInMap = combinedSessionsMap.get(serverSession.sessionId);

                  if (existingSessionInMap) {
                    // Update metadata of the existing session in our map
                    existingSessionInMap.title = serverSession.title;
                    existingSessionInMap.users = serverSession.users || [];
                    existingSessionInMap.activeCanvasPath = canvasRouteMap.get(serverSession.sessionId) ?? serverSession.activeCanvasPath;
                    existingSessionInMap.resource = serverSession.resource;
                    existingSessionInMap.businessObjectId = serverSession.businessObjectId;
                    existingSessionInMap.updatedAt = serverSession.updatedAt; // This is important
                  } else {
                    // New session from server, add its metadata to our map
                    const newSessionForMap = {
                      ...serverSession,
                      activeCanvasPath: canvasRouteMap.get(serverSession.sessionId) ?? serverSession.activeCanvasPath,
                      chatMessages: []
                    }; // chatMessages empty for BehaviorSubject
                    combinedSessionsMap.set(serverSession.sessionId, newSessionForMap);
                  }

                  // Cache the NEW messages received from the server for this session
                  if (serverSession.chatMessages && serverSession.chatMessages.length > 0) {
                    const messagesToCache = serverSession.chatMessages.map(m => ({
                      ...m,
                      sessionId: serverSession.sessionId // Ensure sessionId is on each message
                    }));
                    await this.indexedDbService.cacheChatMessages(messagesToCache);
                  }
                }

                let finalSessionsForSubject = this.applyStoredCanvasPaths(Array.from(combinedSessionsMap.values()), canvasRouteRecords);
                // Sort sessions if necessary, e.g., by updatedAt or createdAt descending
                finalSessionsForSubject.sort((a, b) => (new Date(b.updatedAt || 0).getTime()) - (new Date(a.updatedAt || 0).getTime()));
                await Promise.all(finalSessionsForSubject.map(session => this.indexedDbService.cacheSession(session)));
                this.updateSessionsSubjectIfChanged(finalSessionsForSubject);
              }),
              catchError(error => {
                console.error('Error loading sessions from server:', error);
                // If server fetch fails, rely on cached data if available
                if (cachedSessions.length > 0) {
                  // Process cached sessions for display if server fails
                  this.sessionsSubject.next(this.applyStoredCanvasPaths(
                    cachedSessions.map(s => this.ensureDateObjectsAndProcessSegments(s, false)),
                    canvasRouteRecords
                  ));
                } else if (this.sessionsSubject.getValue().length > 0) { // Don't clear if there was already something from cache
                } else { // Only clear if both cache and server fetch failed and subject was empty
                  this.sessionsSubject.next([]);
                }
                return throwError(() => new Error('Failed to load sessions from server.'));
              })
            );
          }),
          catchError(error => {
            console.error('Error loading sessions from cache:', error);
            this.sessionsSubject.next([]);
            return throwError(() => new Error('Failed to load sessions from cache.'));
          }),
          finalize(() => {
            this.isLoadingSubject.next(false);
            this.initialLoadAttemptedSubject.next(true);
          })
        );
      })
    );
  }

  // Helper to update sessionsSubject only if the new list is different
  private updateSessionsSubjectIfChanged(newSessions: ChatSession[]) {
    const currentSessions = this.sessionsSubject.getValue();
    const currentSignature = currentSessions
      .map(session => `${session.sessionId}:${session.activeCanvasPath || ''}:${new Date(session.updatedAt || 0).getTime()}`)
      .sort()
      .join('|');
    const newSignature = newSessions
      .map(session => `${session.sessionId}:${session.activeCanvasPath || ''}:${new Date(session.updatedAt || 0).getTime()}`)
      .sort()
      .join('|');

    if (currentSignature !== newSignature || currentSessions.length !== newSessions.length) {
      this.sessionsSubject.next(newSessions);
    } else {
      // console.log("Sessions subject not updated as new list is the same.");
    }
  }
  // Helper to ensure createdAt and updatedAt are Date objects and process message segments
  private ensureDateObjectsAndProcessSegments(session: ChatSession, processMessages: boolean = true): ChatSession {
    const chatSession = {
      ...session,
      createdAt: session.createdAt ? new Date(session.createdAt) : undefined,
      updatedAt: session.updatedAt ? new Date(session.updatedAt) : undefined,
      users: session.users || [],
      chatMessages: session.chatMessages || [],
    };
    if (processMessages) {
      // Process all messages in the session using the new centralized method
      chatSession.chatMessages = chatSession.chatMessages.map(msg => this._processAndFinalizeMessage(msg, chatSession.users));
    }
    return chatSession;
  }

  addSession(title: string = 'New Session'): Observable<ChatSession | null> {
    if (!this.currentAppUser || !this.currentAppUser.id) {
      console.error('Cannot add session: no user logged in.');
      return of(null);
    }

    // This payload must remain as specified by the user.
    const newSessionPayloadForBackend = {
      title: title,
      users: [this.currentAppUser, {
        id: 2, // Assuming this is a conventional ID for the Tamkeen
        firebaseId: '0AaE3lU72ffYo9LtpJPYTT0zuRQ2',
        email: 'integration.admin@tamergroup.com',
        fullName: 'Tamkeen'
        // Ensure other required AppUser fields are present or optional in AppUser model
      }]
    } as ChatSession; // Casting to ChatSession implies the backend expects this full structure

    return this.http.post<ChatSession>(this.baseUrl, newSessionPayloadForBackend).pipe(
      map(createdSession => this.ensureDateObjectsAndProcessSegments(createdSession)),
      tap(async newFrontendSession => {
        if (newFrontendSession) {
          await this.indexedDbService.cacheSession(newFrontendSession);
          // Assuming messages are not part of the immediate create response,
          // or if they are, cacheChatMessages would be called.
          // For simplicity, only session metadata is cached here.
          const currentSessions = this.sessionsSubject.getValue();
          this.sessionsSubject.next([...currentSessions, newFrontendSession]);
        }
      }),
      catchError(error => {
        console.error('Error creating session:', error);
        return of(null);
      })
    );
  }

  async closeSession(sessionId: number): Promise<void> {
    try {
      await firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${sessionId}`));
      const currentUserId = this.getCurrentUserId();
      if (currentUserId !== null) {
        await this.indexedDbService.removeSessionCanvasRoute(currentUserId, sessionId);
      }
      await this.indexedDbService.removeCachedSession(sessionId);
      await this.indexedDbService.removeCachedMessagesForSession(sessionId);

      const currentSessions = this.sessionsSubject.getValue().filter(s => s.sessionId !== sessionId);
      this.sessionsSubject.next(currentSessions);
      const nextActiveSessionId = currentSessions[currentSessions.length - 1]?.sessionId ?? null;
      this.activeSessionIdSubject.next(nextActiveSessionId);
      if (nextActiveSessionId !== null) {
        await this.router.navigate(['/ai-studio/session', nextActiveSessionId]);
      } else {
        await this.router.navigate(['/ai-studio']); // Navigate to a default page if no sessions left
      }
    } catch (error) {
      console.error(`Error closing session ${sessionId}:`, error);
      throw error;
    }
  }

  /**
   * Retrieves a specific session by its ID from the server.
   * @param sessionId The numeric ID of the session.
   * @returns An Observable emitting the ChatSession or null if not found or an error occurs.
   */
  getSession(sessionId: number): Observable<ChatSession | null> {
    return this.indexedDbService.dbReady$.pipe(
      filter(ready => ready),
      take(1),
      switchMap(() => {
        if (sessionId === null || sessionId === undefined) {
          console.warn('getSession called with invalid sessionId');
          return of(null);
        }

        return fromPromise(
          Promise.all([
            this.indexedDbService.getCachedSessionWithMessages(sessionId),
            this.getStoredCanvasPathForSession(sessionId)
          ])
        ).pipe(
          // Initial tap to process cached data and update the BehaviorSubject
          tap(([cachedData, storedCanvasPath]) => {
            if (cachedData) {
              const initialSession = this.applyStoredCanvasPath(
                this.ensureDateObjectsAndProcessSegments(cachedData, true),
                storedCanvasPath
              );
              if (initialSession) { // Ensure initialSession is not null
                this.upsertSessionInSubject(initialSession);
                console.log(`ChatSessionService.getSession(${sessionId}): Upserted initial cached data to subject.`);
              }
            } else {
              // This else block can be useful for debugging if cache is unexpectedly empty
              console.log(`ChatSessionService.getSession(${sessionId}): No cached data found for this session initially.`);
            }
          }),
          // mergeMap to handle the HTTP request and final processing
          // It receives the `cachedSessionWithMessages` from the fromPromise
          mergeMap(([cachedSessionWithMessages, storedCanvasPath]) => {
            let latestCachedMessageTimestamp: number | null = null;

            if (cachedSessionWithMessages) {
              // Derive the latest timestamp from the messages if available
              if (cachedSessionWithMessages.chatMessages && cachedSessionWithMessages.chatMessages.length > 0) {
                const lastMessage = cachedSessionWithMessages.chatMessages[cachedSessionWithMessages.chatMessages.length - 1];
                latestCachedMessageTimestamp = new Date(lastMessage.timestamp).getTime();
              }
              // initialSessionData was handled in the preceding tap operator
            }

            // Fetch session details from server, potentially only new messages
            let serverRequestParams = {};
            if (latestCachedMessageTimestamp) {
              serverRequestParams = { lastUpdatedAt: latestCachedMessageTimestamp.toString() };
            }

            console.log(`getSession for ${sessionId}: latestCachedMessageTimestampForThisSession = ${latestCachedMessageTimestamp}`);
            console.log(`getSession for ${sessionId}: serverRequestParams = `, serverRequestParams);

            return this.http.get<ChatSession | null>(`${this.baseUrl}/${sessionId}`, { params: serverRequestParams }).pipe(
              mergeMap(async (serverSessionData) => { // serverSessionData is raw from HTTP
                if (serverSessionData) {
                  const processedServerSession = this.applyStoredCanvasPath(
                    this.ensureDateObjectsAndProcessSegments(serverSessionData, true),
                    storedCanvasPath
                  );

                  // Cache the received server data (metadata and messages)
                  // cacheSession will only store metadata.
                  await this.indexedDbService.cacheSession(processedServerSession);
                  if (processedServerSession.chatMessages && processedServerSession.chatMessages.length > 0) {
                    // Assuming server sends only new messages if lastUpdatedAt was provided,
                    // or all messages if not. cacheChatMessages handles upsert based on messageId.
                    await this.indexedDbService.cacheChatMessages(processedServerSession.chatMessages);
                  }

                  // Re-fetch the complete session from cache to include all messages (old + new)
                  const finalSessionFromCache = await this.indexedDbService.getCachedSessionWithMessages(sessionId);
                  if (finalSessionFromCache) {
                    const fullyProcessedAndConsolidated = this.applyStoredCanvasPath(
                      this.ensureDateObjectsAndProcessSegments(finalSessionFromCache, true),
                      storedCanvasPath
                    );
                    this.upsertSessionInSubject(fullyProcessedAndConsolidated);
                    console.log(`ChatSessionService.getSession(${sessionId}): Server data processed. Emitting consolidated session from cache.`);
                    return fullyProcessedAndConsolidated; // This is what getSession() will emit
                  } else {
                    // This is an unlikely scenario: server returned data, we cached it, but then couldn't retrieve it.
                    // Fallback to the processedServerSession. It might be incomplete if it only contained new messages.
                    console.warn(`ChatSessionService.getSession(${sessionId}): Server data processed, but re-fetch from cache failed. Emitting processed server session.`);
                    this.upsertSessionInSubject(processedServerSession); // Upsert what we got from server
                    return processedServerSession;
                  }
                } else { // serverSessionData is null (e.g., 404 from server)
                  console.log(`ChatSessionService.getSession(${sessionId}): Server returned no data. Emitting null.`);
                  // If the server confirms the session doesn't exist, we might want to remove it from local cache/subject.
                  // For now, getSession() emits null, and AiStudioComponent handles this.
                  // If cached data was previously upserted, it will remain in sessionsSubject until explicitly removed.
                  return null; // getSession() emits null
                }
              })
            );
          }),
          catchError(error => {
            console.error(`Error loading session ${sessionId}:`, error);
            return of(null);
          })
        );
      })
    );
  }

  /**
   * Retrieves a specific chat message by its ID from the server.
   * @param messageId The numeric ID of the message.
   * @returns An Observable emitting the ChatMessage or null if not found.
   */
  getChatMessageFromServer(messageId: number): Observable<ChatMessage | null> {
    if (messageId === null || messageId === undefined) {
      console.error('getChatMessageFromServer called with invalid messageId');
      return of(null);
    }
    // Assuming a new endpoint for fetching a single message
    const messageUrl = `${environment.restApiUrl}chat/messages/${messageId}`;
    return this.http.get<ChatMessage>(messageUrl).pipe(
      map(message => this._processAndFinalizeMessage(message, [])), // Process message, sessionUsers might be incomplete here
      retry({
        count: 25,
        delay: (error, retryCount) => {
          if (error.status === 400) {
            console.log(`Attempt ${retryCount} to fetch message ${messageId}: Retrying after 3 seconds...`);
            return timer(5000);
          }
          return throwError(error);
        }
      }),
      catchError(error => {
        console.error(`Error fetching message ${messageId} after retries:`, error);
        return of(null);
      })
    );
  }

  private upsertSessionInSubject(sessionToUpsert: ChatSession) {
    // Ensure sessionToUpsert is not null or undefined before proceeding
    if (!sessionToUpsert || sessionToUpsert.sessionId === undefined || sessionToUpsert.sessionId === null) {
      console.warn('ChatSessionService.upsertSessionInSubject called with invalid session:', sessionToUpsert);
      return;
    }

    // Always create a new array to ensure BehaviorSubject detects a change
    let newSessionsList = [...this.sessionsSubject.getValue()];
    const index = newSessionsList.findIndex(s => s.sessionId === sessionToUpsert.sessionId);

    if (index > -1) {
      newSessionsList[index] = sessionToUpsert; // Update in the new array
    } else {
      newSessionsList.push(sessionToUpsert); // Add to the new array
    }
    // Sort the new array
    newSessionsList.sort((a, b) => (new Date(b.updatedAt || 0).getTime()) - (new Date(a.updatedAt || 0).getTime()));
    this.sessionsSubject.next(newSessionsList); // Emit the new, sorted array
    console.log(`ChatSessionService.upsertSessionInSubject(${sessionToUpsert.sessionId}): sessionsSubject emitted. New list has ${newSessionsList.length} sessions:`, newSessionsList.map(s => ({ id: s.sessionId, title: s.title, messages: s.chatMessages?.length })));

  }
  // Helper to format titles for display
  formatTitle(title: string): string {
    return title.replace(/\s+/g, '-').toLowerCase(); // Example formatter
  }

  setSessionCanvasPath(sessionId: number, path: string): Observable<ChatSession | null> {
    const currentUserId = this.getCurrentUserId();
    if (currentUserId === null) {
      console.error(`Cannot update canvas path for session ${sessionId}: no current user available.`);
      return of(null);
    }

    const currentSessions = this.sessionsSubject.getValue();
    const sessionIndex = currentSessions.findIndex(s => s.sessionId === sessionId);
    if (sessionIndex === -1) {
      console.error(`Session ${sessionId} not found to update canvas path.`);
      return of(null);
    }

    const normalizedPath = path || 'canvas-view';
    const updatedSession = {
      ...currentSessions[sessionIndex],
      activeCanvasPath: normalizedPath,
    };
    const updatedSessions = [...currentSessions];
    updatedSessions[sessionIndex] = updatedSession;
    this.sessionsSubject.next(updatedSessions);

    return fromPromise((async () => {
      await this.indexedDbService.setSessionCanvasRoute(currentUserId, sessionId, normalizedPath);
      await this.indexedDbService.cacheSession(updatedSession);
      return updatedSession;
    })()).pipe(
      catchError(error => {
        console.error(`Error updating canvas path for session ${sessionId}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Adds a user to an existing chat session on the backend.
   * @param sessionId The ID of the session to update.
   * @param userIdToAdd The ID of the user to add to the session.
   * @returns An Observable emitting the updated ChatSession or null if an error occurs.
   */
  addUserToSession(sessionId: number, userIdToAdd: number): Observable<ChatSession | null> {
    if (sessionId === null || sessionId === undefined || userIdToAdd === null || userIdToAdd === undefined) {
      console.error('ChatSessionService: Invalid sessionId or userIdToAdd for addUserToSession.');
      return of(null);
    }

    // The backend endpoint doesn't require a request body for this specific POST
    return this.http.post<ChatSession>(`${this.baseUrl}/${sessionId}/users/${userIdToAdd}`, null).pipe(
      mergeMap(async serverUpdatedSession => this.applyStoredCanvasPathForCurrentUser(
        this.ensureDateObjectsAndProcessSegments(serverUpdatedSession)
      )),
      tap(async frontendUpdatedSession => {
        if (frontendUpdatedSession) {
          await this.indexedDbService.cacheSession(frontendUpdatedSession); // Cache updated session
          const currentSessions = this.sessionsSubject.getValue();
          const sessionIndex = currentSessions.findIndex(s => s.sessionId === frontendUpdatedSession.sessionId);
          if (sessionIndex > -1) {
            currentSessions[sessionIndex] = frontendUpdatedSession;
            this.sessionsSubject.next([...currentSessions]);
          }
        }
      }),
      catchError(error => {
        console.error(`Error adding user ${userIdToAdd} to session ${sessionId}:`, error);
        // Optionally, you could check error.status here for specific handling, e.g., 400 for IllegalArgumentException
        return of(null); // Return null or throw a more specific error
      })
    );
  }

  /**
   * Adds a received chat message to the appropriate session.
   * This is typically called when a message is received via push notification
   * and needs to be added to the session data if the user isn't actively viewing it.
   * @param receivedMessage The ChatMessage object received.
   */
  addReceivedChatMessageToSession(receivedMessage: ChatMessage): void {
    if (!receivedMessage || typeof receivedMessage.sessionId !== 'number') {
      console.error('ChatSessionService: Invalid received message or missing sessionId', receivedMessage);
      return;
    }

    const currentSessions = this.sessionsSubject.getValue();
    const sessionIndex = currentSessions.findIndex(s => s.sessionId === receivedMessage.sessionId);

    if (sessionIndex > -1) {
      const sessionToUpdate = currentSessions[sessionIndex];

      // Check if the message (by messageId) already exists in the session
      // This is crucial to prevent duplicates from FCM if the API response was already processed.
      // Ensure messageId is valid before checking
      if (receivedMessage.messageId !== undefined && receivedMessage.messageId !== null) {
        const messageExists = sessionToUpdate.chatMessages.some(msg => msg.messageId === receivedMessage.messageId);
        if (messageExists) {
          console.log(`ChatSessionService: Message with ID ${receivedMessage.messageId} already exists in session ${receivedMessage.sessionId}. Skipping add from FCM.`);
          return; // Do not add if it already exists
        }
      } else {
        console.warn('ChatSessionService: Received message without a messageId. Cannot check for duplicates.', receivedMessage);
      }

      // Process the message fully (parsing, segments, etc.)
      const processedMessage = this._processAndFinalizeMessage(receivedMessage, sessionToUpdate.users);

      this.indexedDbService.cacheChatMessage(processedMessage).then(() => console.log("Cached received message"));
      sessionToUpdate.chatMessages.push(processedMessage);
      this.sessionsSubject.next([...currentSessions]); // Emit updated sessions array

      // Announce the newly added message for components to perform specific actions.
      this.messageAddedSubject.next(processedMessage);
    } else {
      // Session is not in memory. Fetch it from the server.
      // getSession will handle caching and updating the sessionsSubject.
      // The received message should be part of the data fetched by getSession.
      this.getSession(receivedMessage.sessionId).pipe(
        // After fetching, the session (including the new message) is in the subject.
        // We can announce the message has been added.
        tap(session => {
          if (session) {
            // Find the specific message to announce it.
            const messageToAnnounce = session.chatMessages.find(m => m.messageId === receivedMessage.messageId);
            if (messageToAnnounce) {
              this.messageAddedSubject.next(messageToAnnounce);
            }
          }
        })
      ).subscribe({
        error: err => console.error(`Failed to fetch new session ${receivedMessage.sessionId} after receiving a message for it.`, err)
      });
    }
  }

  // This new private method centralizes all message processing.
  private _processAndFinalizeMessage(message: ChatMessage, sessionUsers: AppUser[]): ChatMessage {
    // Ensure timestamp is a Date object
    message.timestamp = message.timestamp ? new Date(message.timestamp) : new Date();
    message.origTextContent = message.textContent;

    // --- Logic moved from AiStudioComponent.processChatMessage ---
    if (message.textContent.includes('```json (chart)')) {
      message.chartContent = message.textContent.substring(message.textContent.indexOf('```json (chart)') + 15);
      message.chartContent = message.chartContent.substring(0, message.chartContent.lastIndexOf('```'));
      try {
        const parsedData = JSON.parse(message.chartContent);
        if (typeof parsedData === 'object' && parsedData !== null) {
          message.chartContent = parsedData;
        } else {
          message.chartContent = null;
        }
      } catch (e) {
        console.error("Error parsing chart JSON:", e);
        message.chartContent = null;
      }
      let textContent = message.textContent.substring(0, message.textContent.indexOf('```json (chart)'));
      textContent = textContent + message.textContent.substring(message.textContent.lastIndexOf('```') + 3);
      message.textContent = textContent;
    }
    // --- End of moved logic ---

    // Create rich text segments
    message.segments = this.convertToMessageSegments(message.textContent, sessionUsers);

    /*if (message.messageId === 361) {
      message.approvalRequest = {
        promoPlanId: 15,
        promoDescription: 'Sample Promo Plan',
        approvers: [
          {
            userId: 1,
            userName: 'Mohammed Mukhtar',
            userAvatarUrl: 'https://lh3.googleusercontent.com/a/ACg8ocLsA4P4F74spk34AzsfqxY9LM8zs_sTWeNIlSM1JEh53-layYTn=s576-c-no',
            approved: true,
            timestamp: new Date()
          },
          {
            userId: 4,
            userName: 'Mahmood AlRae',
            userAvatarUrl: 'https://lh3.googleusercontent.com/a/ACg8ocLrt7sr43FPbi-zaXSRnqKAnfYwp-oXujowAe2VwF8BgRLLe5o=s96-c',
            approved: false,
            timestamp: new Date()
          }
        ]
      }
    }*/

    return message;
  }

  searchSession(resourceId: number, businessObjectId: number): Observable<ChatSession | null> {
    const params = {
      resourceId: resourceId.toString(),
      businessObjectId: businessObjectId.toString()
    };
    return this.http.get<ChatSession>(`${this.baseUrl}/search`, { params }).pipe(
      mergeMap(async session => this.applyStoredCanvasPathForCurrentUser(
        this.ensureDateObjectsAndProcessSegments(session)
      )),
      tap(async session => {
        if (session) {
          await this.indexedDbService.cacheSession(session);
          this.upsertSessionInSubject(session);
        }
      }),
      catchError(error => {
        console.error(`Error searching for session with resourceId ${resourceId} and businessObjectId ${businessObjectId}:`, error);
        return of(null);
      })
    );
  }

  ngOnDestroy() {
    this.authSubscription.unsubscribe();
  }

  // Renamed and refactored to produce segments
  convertToMessageSegments(rawText: string, sessionUsers: AppUser[] = []): MessageSegment[] {
    let text = rawText;
    // 1. Headings (H2)
    text = text.replace(/## (.+)/g, "<h2>$1</h2>");

    // 2. Bold text (convert to strong for semantic correctness)
    text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

    // 3. Delivery Partner List (handling * as list items)
    text = text.replace(/\* (.+?) \*/g, "<ul><li>$1</li></ul>"); //For single list

    text = text.replace(/\* (.+)/g, "<li>$1</li>"); //For multiple list

    if (text.includes("<li>")) {
      const listItems = text.split("\n").filter(line => line.startsWith("<li>"));
      if (listItems.length > 1) {
        text = text.replace(/\* (.+)/g, ""); // clear old list
        text = text.replace(listItems.join(""), `<ul>${listItems.join("")}</ul>`); // wrap all <li> to <ul>
      }
    }

    // 5. Code Blocks
    const codeBlockRegex = /```(\w+)?\n([\s\S]+?)\n```/g;
    text = text.replace(codeBlockRegex, "");

    // 6. Images
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
    text = text.replace(imageRegex, (match, alt, url) => {
      return `<img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;">`;
    });

    // 7. Links (Markdown)
    const linkRegex = /\[(.*?)\]\((.*?)\)/g;
    text = text.replace(linkRegex, (match, linkText, url) => {
      return `<a href="${url}" target="_blank">${linkText}</a>`;
    });

    // 8. Links (Plain URLs)
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    text = text.replace(urlRegex, (match, url) => {
      return `<a href="${url}" target="_blank">${url}</a>`;
    });

    // 9. Blockquotes
    const blockquoteRegex = /^>(.*)$/gm; // 'gm' for multiline
    text = text.replace(blockquoteRegex, (match, quote) => {
      return `<blockquote>${quote}</blockquote>`;
    });

    // 10. Horizontal Rules
    const hrRegex = /^(---|(\*\*\*))$/gm;
    text = text.replace(hrRegex, '<hr>');

    // 11. File Upload
    const fileRegex = /\[(.*?)\]\((.*?)\)/g;
    text = text.replace(fileRegex, (match, fileName, fileUrl) => {
      return `<a href="${fileUrl}" target="_blank" class="file-link">${fileName}</a>`;
    });

    // 12. Newlines to <br> for other text (except headings and lists which are already block elements)
    text = text.replace(/(?<!<h.>)(?<!<ul.>)(?<!<\/ul>)(?<!<table.+)(?<!<\/table>)(?<!<pre.>)(?<!<\/pre>)(?<!<img.+)(?<!<a.+)(?<!<\/a>)(?<!<blockquote.+)(?<!<\/blockquote>)\n/g, "<br>");

    // 13. Convert @mentions to MessageSegment objects
    // New Format: @{DisplayName}
    const segments: MessageSegment[] = [];
    const mentionRegex = /@\{([^}]+)\}/g;
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const displayName = match[1];
      // Add text part before the mention
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: this.sanitizer.sanitize(SecurityContext.HTML, text.substring(lastIndex, match.index)) || ''
        });
      }

      // Find the user
      const mentionedUser = sessionUsers.find(u => u.fullName === displayName);
      if (mentionedUser) {
        segments.push({ type: 'mention', user: mentionedUser });
      } else {
        // If user not found, treat as plain text (or handle as an unknown mention)
        segments.push({ type: 'text', content: this.sanitizer.sanitize(SecurityContext.HTML, match[0]) || '' });
      }
      lastIndex = mentionRegex.lastIndex;
    }

    // Add any remaining text after the last mention
    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        content: this.sanitizer.sanitize(SecurityContext.HTML, text.substring(lastIndex)) || ''
      });
    }

    return segments.length > 0 ? segments : [{ type: 'text', content: this.sanitizer.sanitize(SecurityContext.HTML, text) || '' }];
  }
}
